import { debounce, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { Collapse } from '@material-ui/core';
import { useRef } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';
import { getFaqs } from '../../../redux-store/actions/faq';
import { FaqData, FaqDataInfo } from '../../../redux-store/types/faq';
import en from '../../../lang/en-us';
import { FaqLinks, isMobileDevice } from '../../../utils/constants';
import { RootStateType } from '../../../redux-store/reducers';
import { getQueryParams } from '../../../utils/utilityFunctions';

const { common } = en;

type LocationProps = {
    section?: string;
    faqs?: FaqData[];
};
interface FaqProps extends RouteComponentProps<any, any, LocationProps> {
    getFaqs: () => Promise<FaqData>;
}

export const RenderSection = ({
    sections = [],
    sectionLabel,
    sectionId,
    publicRoute = false,
}: {
    sections: FaqDataInfo[];
    sectionLabel: string;
    sectionId: string;
    publicRoute?: boolean;
}) => {
    const [collapseItems, setCollapseItems] = useState<string[]>([]);
    const history = useHistory();
    const { isLoggedIn = false } = useSelector((state: RootStateType) => state.authReducer);

    const _toggleCollapse = (question: string) => {
        const index = collapseItems.findIndex((_question) => question === _question);
        if (index === -1) {
            setCollapseItems((prev) => [...prev, question]);
            return;
        }
        setCollapseItems((prev) => prev.filter((_, _index) => _index !== index));
    };
    return (
        <div id={sectionLabel} className="faq-info">
            <h3>{sectionLabel}</h3>
            {sections.map((section, index) => {
                const open = collapseItems.includes(section.question);
                return (
                    <div
                        onClick={() => _toggleCollapse(section.question)}
                        className="faq-info-txt"
                        key={index + sectionLabel}
                    >
                        <h4>
                            {section.question}
                            <img
                                src={
                                    !open
                                        ? '/images/down-arrow-black.svg'
                                        : '/images/up-arrow-black.svg'
                                }
                                alt="icon"
                            />
                        </h4>
                        <Collapse in={open} unmountOnExit timeout="auto">
                            <p>
                                {section.answer}{' '}
                                {section.linkRequired === 'Yes' &&
                                    (isLoggedIn ? !publicRoute : true) && (
                                        <span
                                            role="button"
                                            onClick={() => {
                                                isMobileDevice()
                                                    ? history.push(
                                                          section.linkUrl
                                                              ? section.linkUrl
                                                              : FaqLinks[sectionId],
                                                      )
                                                    : window.open(
                                                          section.linkUrl
                                                              ? `${window.location.origin}${section.linkUrl}`
                                                              : `${window.location.origin}${FaqLinks[sectionId]}`,
                                                          '_blank',
                                                      );
                                            }}
                                            style={{ cursor: 'pointer', color: '#836fe0' }}
                                        >
                                            Click here
                                        </span>
                                    )}
                                {section.tableRequired === 'Y' && section.tableData && (
                                    <>
                                        <RenderTable {...section.tableData} />
                                        <p>{section.tableAnswer}</p>
                                    </>
                                )}
                                {section.listRequired === 'Y' && section.listItems && (
                                    <div>
                                        <ul>
                                            {section.listItems.map((item) => {
                                                return (
                                                    <li>
                                                        {' '}
                                                        <p> {item}</p>{' '}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </p>
                        </Collapse>
                    </div>
                );
            })}
        </div>
    );
};

const RenderTable = ({ columns, rows }: { columns: string[]; rows: string[][] }) => {
    return (
        <Table>
            <TableHead>
                <TableRow>
                    {columns.map((column) => {
                        return <TableCell style={{ fontSize: 12 }}> {column} </TableCell>;
                    })}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => {
                    return (
                        <TableRow>
                            {row.map((rowValue) => {
                                return <TableCell style={{ fontSize: 12 }}> {rowValue}</TableCell>;
                            })}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

export const isMobile = (userAgent: string) => {
    if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
        return true;
    }
    return false;
};

function Faq(props: FaqProps) {
    const dispatch = useDispatch();
    const [_section = ''] = getQueryParams(props.location.search);
    const { section = _section } = props.location.state || {};
    const [faqs, setFaqs] = useState<FaqData[]>([]);
    const [selector, setSelector] = useState<string>(section);
    const scrollRef = useRef<any>(null);
    const enableScroll = useRef<boolean>(true);
    const { isLoggedIn = false } = useSelector((state: RootStateType) => state.authReducer);

    useEffect(() => {
        async function callApi() {
            const response: any = await dispatch(getFaqs());
            const { body = {} } = response || {};
            const faqs: FaqData[] = Object.keys(body).map((key) => {
                return {
                    sections: [...body[key]],
                    sectionLabel: key,
                    sectionId: body[key][0]?.sectionId,
                    publicRoute: body[key][0]?.publicRoute,
                };
            });
            setSelector((section) => (section ? section : faqs[0]?.sectionId));
            setFaqs(faqs);
        }
        callApi();
    }, []);

    useEffect(() => {
        const _isMobile = isMobile(window.navigator.userAgent);
        if (faqs.length) {
            const index = faqs.findIndex((faq) => faq.sectionId === selector);
            enableScroll.current = false;
            if (!_isMobile) {
                scrollRef.current?.children[index]?.scrollIntoView({
                    behavior: 'smooth',
                });
            } else {
                if (!!section) {
                    props.history.replace('faq-section', { faqs: [faqs[index]] });
                }
            }
        }
    }, [selector, faqs]);

    const _scrollToContent = (index: number) => {
        setSelector(faqs[index].sectionId);
    };

    const moveTo = (index: number) => {
        props.history.push('faq-section', { faqs: [faqs[index]] });
    };

    const _onScroll = debounce((e: any) => {
        if (enableScroll.current) {
            const { scrollTop } = e?.target;
            let position = 0;
            let section = faqs.find((_, index) => {
                const { height } = scrollRef.current?.children[index].getBoundingClientRect();
                if (scrollTop < height + position) {
                    return true;
                }
                position += height;
                return false;
            });
            if (section) {
                setSelector(section.sectionId);
            }
        }
        enableScroll.current = true;
    }, 100);
    return (
        <div className="curved-bg faq-curved">
            <h2 className="heading">
                <a onClick={() => props.history.goBack()}>
                    {isMobile(window.navigator.userAgent) || isMobileDevice() ? (
                        <img src="/images/left-arrow-black.svg" alt="icon" />
                    ) : null}
                </a>
                {common.FAQ}
            </h2>
            <div className="curved-bg-content desktop_mode">
                <div className="faq-content-outer">
                    <div className="faq-content-left">
                        <div className="faq-content-left-list">
                            {faqs.map((faq, index) => (
                                <a
                                    className={selector === faq.sectionId ? 'active' : ''}
                                    key={index}
                                    onClick={() => _scrollToContent(index)}
                                >
                                    {faq.sectionLabel}
                                </a>
                            ))}
                        </div>
                    </div>
                    <div onScroll={_onScroll} className="faq-content-right">
                        <div className="faq-content-right-content">
                            <div className="faq-list" ref={scrollRef}>
                                {faqs.map((faq, index) => (
                                    <RenderSection key={index} {...faq} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="faq-mobile mobile_mode">
                <div className="faq-sections">
                    <div className="faq-sections-list">
                        {faqs.map((faq, index) => (
                            <a
                                className={selector === faq.sectionId ? 'active' : ''}
                                key={index}
                                onClick={() => moveTo(index)}
                            >
                                {faq.sectionLabel} <img src="/images/right-arrow.svg" alt="icon" />
                            </a>
                        ))}
                    </div>
                    {/* <div className="faq-answers">
                        <div className="faq-list" ref={scrollRef}>
                            {faqs.map((faq, index) => (
                                <RenderSection key={index} {...faq} />
                            ))}
                        </div>
                    </div> */}
                </div>
            </div>
        </div>
    );
}
export default Faq;
